import { Email as EmailValidation } from '@hc-frontend/core-types';
import { ErrorMessage } from '@hc-frontend/core-ui';
import { ButtonWithTracking } from '@hc-frontend/shells-shared';
import { Dispatch, FormEvent, SetStateAction, useState } from 'react';
import { z } from 'zod';

import { EMAIL, ResultStatus } from './constants';
import { getSubsidyAction } from './get-subsidy-action';
import { useSubsidyCalculator } from './provider';

const FormSchema = z.object({
  email: EmailValidation,
});

type EmailViewProps = {
  minLoadingTime: number;
  setStatus: Dispatch<SetStateAction<ResultStatus>>;
};

export function EmailView({ setStatus, minLoadingTime }: EmailViewProps) {
  const { answers, setAnswer, setSubsidy } = useSubsidyCalculator();
  const [email, setEmail] = useState(answers[EMAIL] ?? '');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    const validation = FormSchema.safeParse({ email });
    if (!validation.success) {
      setError('Please enter a valid email.');
      return;
    }

    setAnswer(EMAIL, validation.data.email);
    setStatus('loading');

    // At least wait for the minLoadingTime to show all the loading animations.
    const [subsidyPromise] = await Promise.allSettled([
      getSubsidyAction({ answers }),
      new Promise((r) => setTimeout(r, minLoadingTime)),
    ]);

    if (subsidyPromise.status !== 'fulfilled') {
      setStatus('error');
      return;
    }

    if (subsidyPromise.value.result !== 'success') {
      setStatus('error');
      return;
    }

    setSubsidy(subsidyPromise.value.body);
    setStatus('success');
  };

  return (
    <div className="bg-gradient-to-br from-tertiary-900 to-primary-900 rounded-2xl p-24-32 animate-in fade-in slide-in-from-bottom-8-8 duration-1000 fill-mode-both">
      <div className="flex justify-center items-center flex-col max-w-[620px] mx-auto">
        <p className="hc-typography--heading-three text-gray-0 text-center">
          Get Your Results
        </p>
        <p className="hc-typography--paragraph-sm text-gray-0 text-center">
          Enter your email to see your subsidy amount and potential savings
        </p>

        <form
          onSubmit={handleSubmit}
          className="w-full grid grid-cols-1 tablet:grid-cols-[2fr_1fr] mt-24-24 gap-8-8"
        >
          <label className="hc-form-control relative">
            <input
              placeholder="Enter your email"
              aria-label="Email"
              type="email"
              name={EMAIL}
              className="w-full outline-none"
              onChange={(e) => setEmail(e.target.value)}
              value={email}
            />
          </label>

          <ButtonWithTracking
            id="next-button"
            variant="primary-card-small"
            type="submit"
            className="w-full px-32-48 whitespace-nowrap flex justify-center"
            metricTags={{}}
            name="action"
            value="results"
          >
            Get Results
          </ButtonWithTracking>
        </form>

        {error ? (
          <div className="mt-4-4">
            <ErrorMessage>{error}</ErrorMessage>
          </div>
        ) : null}

        <p className="hc-typography--disclaimer text-gray-0 mt-16-16 text-center">
          By providing your email, you agree to receive communications from
          Healthcare.com about health insurance options, savings opportunities,
          and related services. You can unsubscribe at any time.
        </p>
      </div>
    </div>
  );
}
